'use client'

// เมนูติดต่อลอยของเซลเพจ (LINE / Facebook / Instagram / TikTok / โทร)
//
// เดิมเป็น "แถวไอคอนกางค้างไว้" คู่กับปุ่มตะกร้าลอย ซึ่งซ้ำกับตะกร้าบน navbar
// อยู่แล้ว — ตอนนี้เหลือปุ่มเดียว กดแล้วช่องทางติดต่อค่อยกางขึ้นด้านบน
// (กดซ้ำ/กดที่ว่าง/กด Esc = หุบ) ไอคอนช่องทางใช้รูปวงกลมสำเร็จรูปของแบรนด์
// (มีขอบ/พื้นหลังในตัว) ส่วนปุ่มหลักเป็นกราฟิกขาวโปร่งจึงยังต้องมีพื้นหลัง+ขอบขาว

import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'

interface ShopSocials {
  phone?: string | null
  facebookUrl?: string | null
  instagramUrl?: string | null
  tiktokUrl?: string | null
  lineId?: string | null
}

const normUrl = (u: string) => (/^https?:/i.test(u) ? u : `https://${u.replace(/^\/+/, '')}`)

interface IconVisibility {
  line?: boolean
  facebook?: boolean
  instagram?: boolean
  tiktok?: boolean
  phone?: boolean
}

/** ขอบขาวรอบไอคอน — ค่าเดียวใช้ทั้งปุ่มหลักและลูก */
const RING = 3

/** ขนาดปุ่ม = ปุ่มตะกร้าบน navbar (w-10 h-10 = 40px) ให้ดูเป็นชุดเดียวกัน */
const BTN = 'w-10 h-10'

/** สีพื้นหลังปุ่มหลักเมื่อร้านไม่ได้ตั้งเอง */
const DEFAULT_BUTTON_COLOR = '#2563EB'

export default function FloatingSocials({
  shop,
  position = 'left',
  visible,
  buttonColor,
}: {
  shop: ShopSocials
  position?: 'left' | 'right'
  // เปิด/ปิดรายช่องต่อเพจ — ไม่ได้ตั้ง = แสดง (เพจเดิมไม่เปลี่ยนพฤติกรรม)
  // เฉพาะค่า false เท่านั้นที่ซ่อน
  visible?: IconVisibility | null
  /** สีพื้นหลังปุ่มหลัก (ตั้งต่อเพจในแท็บ "ติดต่อ") */
  buttonColor?: string | null
}) {
  const vis = visible ?? {}
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  // ปิดเมื่อกดที่อื่น / กด Esc — เมนูลอยอยู่เหนือเนื้อหา ถ้าไม่ปิดให้จะบังปุ่มอื่น
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // ไอคอนชุด /icons/socials/* เป็นวงกลมมาในรูปอยู่แล้ว (มีขอบ + พื้นหลังของ
  // แบรนด์ในตัว + ขอบรูปโปร่ง) → ไม่ต้องใส่ ring/พื้นหลังทับ ใช้เงาเบาๆ พอ
  // Instagram ยังเป็นไฟล์เดิมซึ่งเป็นสี่เหลี่ยมเต็มขอบ ต้อง mask เป็นวงกลมเอง
  const items: Array<{
    key: string; href: string; img: string; label: string; external: boolean
    /** true = รูปเต็มขอบ ต้องครอบให้กลมเอง (ไฟล์เดิม), false = รูปกลมมาแล้ว */
    maskRound?: boolean
  }> = []

  if (shop.lineId && vis.line !== false) items.push({ key: 'line', href: `https://line.me/R/ti/p/${shop.lineId}`, img: '/icons/socials/line.png', label: 'LINE', external: true })
  if (shop.facebookUrl && vis.facebook !== false) items.push({ key: 'fb', href: normUrl(shop.facebookUrl), img: '/icons/socials/fb.png', label: 'Facebook', external: true })
  if (shop.instagramUrl && vis.instagram !== false) items.push({ key: 'ig', href: normUrl(shop.instagramUrl), img: '/icons/ig.webp', label: 'Instagram', external: true, maskRound: true })
  if (shop.tiktokUrl && vis.tiktok !== false) items.push({ key: 'tiktok', href: normUrl(shop.tiktokUrl), img: '/icons/socials/tiktok.png', label: 'TikTok', external: true })
  if (shop.phone && vis.phone !== false) items.push({ key: 'phone', href: `tel:${shop.phone}`, img: '/icons/socials/phone.png', label: 'โทร', external: false })

  if (items.length === 0) return null

  // ไม่มีปุ่มตะกร้าลอยแล้ว (ตะกร้าอยู่บน navbar อย่างเดียว) → วางชิดล่างได้ทั้ง 2 ฝั่ง
  const posClass = position === 'right' ? 'right-3 bottom-3 items-end' : 'left-3 bottom-3 items-start'

  const ringStyle = { boxShadow: `0 0 0 ${RING}px #fff, 0 6px 16px rgba(0,0,0,.18)` }

  return (
    <div ref={wrapRef} className={`fixed ${posClass} z-40 flex flex-col gap-3`}>
      {/* ช่องทางติดต่อ — กางขึ้นด้านบนของปุ่มหลัก */}
      {open && (
        <div className={`flex flex-col gap-3 ${position === 'right' ? 'items-end' : 'items-start'}`}>
          {items.map((it, i) => (
            <a
              key={it.key}
              href={it.href}
              {...(it.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              aria-label={it.label}
              title={it.label}
              onClick={() => setOpen(false)}
              className={`${BTN} flex items-center justify-center transition-transform hover:scale-110 active:scale-95 animate-social-in ${it.maskRound ? 'rounded-full overflow-hidden' : ''}`}
              style={{
                // เงาตามรูปทรงของไอคอน (ไม่ใช่กรอบสี่เหลี่ยม) — ไอคอนกลมอยู่แล้ว
                // เลยไม่ต้องมีขอบขาวครอบอีกชั้น
                filter: 'drop-shadow(0 4px 10px rgba(0,0,0,.22))',
                // ไล่ขึ้นทีละตัวจากล่างไปบน ให้ดูเป็นชุดเดียวกับปุ่มหลัก
                animationDelay: `${(items.length - 1 - i) * 45}ms`,
              }}
            >
              <Image
                src={it.img}
                alt={it.label}
                width={40}
                height={40}
                className={`w-full h-full ${it.maskRound ? 'object-cover' : 'object-contain'}`}
              />
            </a>
          ))}
        </div>
      )}

      {/* ปุ่มหลัก — ปิดอยู่โชว์ไอคอนแชท (ไฟล์รูปสีขาวโปร่ง จึงต้องมีพื้นหลังสี
          เสมอ ไม่งั้นมองไม่เห็น), เปิดแล้วเปลี่ยนเป็นกากบาท */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={open ? 'ปิดเมนูติดต่อ' : 'ช่องทางติดต่อ'}
        title="ช่องทางติดต่อ"
        className={`${BTN} rounded-full flex items-center justify-center transition-transform hover:scale-105 active:scale-95`}
        style={{ ...ringStyle, backgroundColor: buttonColor || DEFAULT_BUTTON_COLOR }}
      >
        {open ? (
          <svg viewBox="0 0 24 24" className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
          </svg>
        ) : (
          <Image src="/icons/speech.png" alt="" width={40} height={40} className="w-5 h-5 object-contain" aria-hidden="true" />
        )}
      </button>
    </div>
  )
}
